import { buildURL } from 'imgix-url-builder'
import type { ImageLoaderProps } from 'next/image'

/**
 * Imgix loader for Prismic images.
 *
 * SVGs are not resized by `w=` / `fit=max` the way rasters are — Imgix still returns
 * the full SVG (we saw ~6.5MB/request for one asset). Next.js srcset then multiplies
 * that cost across device widths (640…3840). For SVGs, always return one canonical URL.
 */
export function imgixLoader({ src, width, quality }: ImageLoaderProps): string {
  if (isPrismicSvgUrl(src)) {
    return canonicalAssetUrl(src)
  }

  const params: Record<string, string | number> = {
    fit: safeSearchParam(src, 'fit') || 'max',
    w: width,
  }

  if (quality) {
    params.q = quality
  }

  return buildURL(src, params)
}

/** True when the asset URL path ends in `.svg` (querystring ignored). Extensionless URLs are treated as non-SVG. */
export function isPrismicSvgUrl(src: string): boolean {
  try {
    const pathname = new URL(src).pathname
    return /\.svg$/i.test(pathname)
  } catch {
    return /\.svg(\?|$)/i.test(src)
  }
}

function canonicalAssetUrl(src: string): string {
  try {
    const url = new URL(src)
    return `${url.origin}${url.pathname}`
  } catch {
    return src.split('?')[0] ?? src
  }
}

function safeSearchParam(src: string, key: string): string | null {
  try {
    return new URL(src).searchParams.get(key)
  } catch {
    return null
  }
}
